import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { FileSignature, Eye, Send, Trash2, ShieldX, Loader2, Plus, Users, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { contractsAPI, industryLettersAPI, restaurantsAPI } from '../lib/api';
import ContractDocument from './ContractDocument';
import { ReissueLetterButton, ReissueNote } from './ReissueLetterButton';
import { LETTER_PILL } from './CycleBoard';

const CYCLE_STATUS = {
  draft: 'bg-gray-100 text-gray-700',
  active: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-blue-100 text-blue-700',
  archived: 'bg-amber-50 text-amber-800',
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : '');

const LetterRow = ({ c, onView, onSend, onCopy, onVoid, onDelete, onRefresh, onReissued, canWrite }) => {
  const pill = LETTER_PILL[c.status] || LETTER_PILL.draft;
  const live = c.status !== 'signed' && c.status !== 'voided';
  return (
    <div data-testid={`industry-letter-row-${c.id}`} className="border rounded p-2.5 bg-white">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{c.template_name || 'Industry Letter'}</p>
          <p className="text-[11px] text-gray-500 truncate">
            Created {fmt(c.created_at)} · {c.student_count ?? 0} student{c.student_count === 1 ? '' : 's'} listed
            {c.signed_at && <> · signed {fmt(c.signed_at)} by <strong>{c.signer_name}</strong></>}
          </p>
          <ReissueNote contract={c} />
        </div>
        <span data-testid={`industry-letter-status-${c.id}`} className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${pill.cls}`}>
          <pill.Icon className="w-3 h-3" /> {c.status}
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-1">
        <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`industry-letter-view-${c.id}`} onClick={() => onView(c)}><Eye className="w-3.5 h-3.5 mr-1" /> View</Button>
        {live && (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-700 hover:bg-blue-50" data-testid={`industry-letter-send-${c.id}`} onClick={() => onSend(c)}>
            <Send className="w-3.5 h-3.5 mr-1" /> {c.status === 'draft' ? 'Send to chef' : 'Resend'}
          </Button>
        )}
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onCopy(c)}>Copy link</Button>
        {live && (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-gray-600" data-testid={`industry-letter-refresh-${c.id}`} title="Re-sync students and cycle phases" onClick={() => onRefresh(c)}>
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        )}
        {live && (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50 ml-auto" onClick={() => onVoid(c)}>
            <ShieldX className="w-3.5 h-3.5 mr-1" /> Void
          </Button>
        )}
        {canWrite && <ReissueLetterButton contract={c} onDone={onReissued} className="ml-auto" />}
        {c.status === 'draft' && (
          <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50" data-testid={`industry-letter-delete-${c.id}`} onClick={() => onDelete(c)}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * Restaurant-scoped Industry Placement Letter manager — one letter per
 * placement cycle. Lists every cycle the restaurant takes part in with that
 * cycle's letter(s), and lets staff generate / send / void / delete them.
 */
const IndustryLetterPanel = ({ restaurant, focusCycleId = null, canWrite = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creatingFor, setCreatingFor] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [viewingFull, setViewingFull] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await restaurantsAPI.placementsByCycle(restaurant.id);
      setData(res.data);
    } catch {
      toast.error('Failed to load industry letters');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (restaurant?.id) reload(); /* eslint-disable-next-line */ }, [restaurant?.id]);

  const handleCreate = async (cycle, allowDuplicate = false) => {
    setCreatingFor(cycle.id);
    try {
      await industryLettersAPI.create({ restaurant_id: restaurant.id, cycle_id: cycle.id, allow_duplicate: allowDuplicate });
      toast.success(`Industry letter generated for ${cycle.name} (draft)`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally {
      setCreatingFor(null);
    }
  };

  const handleSend = async (c) => {
    try {
      const res = await contractsAPI.send(c.id, { base_url: window.location.origin });
      const cc = res.data?.cc || [];
      toast.success(cc.length ? `Sent to ${res.data?.recipient} (cc: ${cc.join(', ')})` : `Sent to ${res.data?.recipient}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    }
  };
  const handleVoid = async (c) => {
    if (!window.confirm('Void this industry letter?')) return;
    try { await contractsAPI.void(c.id); toast.success('Voided'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Void failed'); }
  };
  const handleRefresh = async (c) => {
    try { await contractsAPI.refresh(c.id); toast.success('Letter re-synced with students and cycle phases'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Refresh failed'); }
  };
  const handleDelete = async (c) => {
    if (!window.confirm('Delete this draft permanently?')) return;
    try { await contractsAPI.delete(c.id); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  const openView = async (c) => {
    setViewing(c); setViewingFull(null);
    try { const r = await contractsAPI.get(c.id); setViewingFull(r.data); }
    catch { toast.error('Failed to load'); }
  };
  const copyLink = async (c) => {
    const url = `${window.location.origin}/contract/sign/${c.access_token}`;
    try { await navigator.clipboard.writeText(url); toast.success('Signing link copied'); }
    catch { toast.error('Copy failed'); }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-ica-navy" />
      </div>
    );
  }

  const rowProps = { onView: openView, onSend: handleSend, onCopy: copyLink, onVoid: handleVoid, onDelete: handleDelete, onRefresh: handleRefresh, onReissued: reload, canWrite };
  const groups = data.groups || [];
  const legacy = data.legacy_industry_letters || [];

  return (
    <div data-testid="industry-letter-panel" className="space-y-4">
      <div className="flex items-center gap-2">
        <FileSignature className="w-4 h-4 text-ica-navy" />
        <h3 className="font-semibold text-sm text-ica-navy">Industry Placement Confirmation Letters</h3>
      </div>
      <p className="text-xs text-gray-500">
        One letter per placement cycle, listing every student placed at this establishment in that cycle.
        A letter signed for one cycle never counts for another.
      </p>

      {groups.length === 0 && legacy.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-3" data-testid="industry-letter-empty">
          This restaurant isn't part of any placement cycle yet.
        </p>
      )}

      {groups.map((g) => {
        const letters = g.industry_letters || [];
        const live = g.industry_letter;
        const pill = LETTER_PILL[live?.status || 'not_created'] || LETTER_PILL.not_created;
        const focused = focusCycleId === g.cycle.id;
        return (
          <div
            key={g.cycle.id}
            data-testid={`industry-cycle-group-${g.cycle.id}`}
            className={`rounded-md border bg-gray-50/60 p-3 space-y-2 ${focused ? 'ring-2 ring-ica-gold/60' : ''}`}
          >
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-ica-navy text-sm truncate">{g.cycle.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${CYCLE_STATUS[g.cycle.status] || CYCLE_STATUS.draft}`}>{g.cycle.status}</span>
                </div>
                <div className="text-[11px] text-gray-500 flex items-center gap-2 mt-0.5">
                  <span>{g.cycle.start_date} → {g.cycle.end_date}</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {g.filled} student{g.filled === 1 ? '' : 's'} placed</span>
                </div>
              </div>
              <span
                data-testid={`industry-cycle-status-${g.cycle.id}`}
                className={`inline-flex items-center gap-1 text-[11px] font-medium rounded px-2 py-0.5 ${pill.cls}`}
              >
                <pill.Icon className="w-3 h-3" /> {pill.label}
              </span>
            </div>

            {letters.length > 0 && (
              <div className="space-y-2">
                {letters.map((c) => <LetterRow key={c.id} c={c} {...rowProps} />)}
              </div>
            )}

            {canWrite && (
              live ? (
                <button
                  type="button"
                  data-testid={`industry-generate-another-${g.cycle.id}`}
                  className="text-[11px] text-gray-500 hover:text-ica-navy underline"
                  disabled={creatingFor === g.cycle.id}
                  onClick={() => { if (window.confirm(`Generate an additional letter for ${g.cycle.name}? The existing ${live.status} letter stays as is.`)) handleCreate(g.cycle, true); }}
                >
                  Generate another letter for this cycle
                </button>
              ) : (
                <Button
                  data-testid={`industry-generate-${g.cycle.id}`}
                  size="sm"
                  disabled={creatingFor === g.cycle.id || g.filled === 0}
                  title={g.filled === 0 ? 'No students placed here in this cycle yet' : undefined}
                  onClick={() => handleCreate(g.cycle)}
                >
                  {creatingFor === g.cycle.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Generate letter
                </Button>
              )
            )}
          </div>
        );
      })}

      {legacy.length > 0 && (
        <div className="rounded-md border border-dashed p-3 space-y-2" data-testid="industry-legacy-group">
          <p className="text-xs font-semibold text-gray-600">Letters not linked to a cycle</p>
          {legacy.map((c) => <LetterRow key={c.id} c={c} {...rowProps} />)}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) { setViewing(null); setViewingFull(null); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.template_name}</DialogTitle>
            <DialogDescription>
              {viewing?.status === 'signed'
                ? `Signed on ${new Date(viewing?.signed_at).toLocaleString()} by ${viewing?.signer_name}`
                : `Status: ${viewing?.status}`}
            </DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3">
              {viewing.status !== 'signed' && (
                <a
                  href={`${window.location.origin}/contract/sign/${viewing.access_token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-sm text-ica-navy underline"
                >
                  Open public signing page →
                </a>
              )}
              {viewingFull ? (
                <ContractDocument
                  bodyHtml={viewingFull.template_snapshot?.body_html || ''}
                  fieldValues={viewingFull.field_values || {}}
                  signatures={{
                    signer: viewingFull.signer_signature_data,
                    witness: viewingFull.witness_signature_data,
                    ica_signer: viewingFull.ica_signature_data,
                  }}
                  logoUrl={viewingFull.logo_url}
                  senderBlockHtml={viewingFull.template_snapshot?.sender_block_html}
                />
              ) : (
                <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-ica-navy" /></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setViewing(null); setViewingFull(null); }}>Close</Button>
            {viewing?.status === 'signed' && (
              <Button onClick={() => window.open(`/contract/sign/${viewing.access_token}`, '_blank')} >
                Open & Print
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IndustryLetterPanel;
